import React, { useState, useEffect } from 'react';
import {
    Dialog, Button, Alert, CircularProgress, Box, Select, MenuItem, TextField, Typography, Divider, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReservationService } from '../api/services/reservationService';
import { SettingsService } from '../api/services/settingsService';
import { useCurrency, formatCurrency } from '../utils/currency';

export default function CancelReservationModal({ open, onClose, reservation, tenantId, onSuccess, setNotification, currency: explicitCurrency }) {
    const { currency, currencySymbol } = useCurrency(explicitCurrency || reservation?.currency);
    const [isCancelling, setIsCancelling] = useState(false);
    const [error, setError] = useState(null);

    const [policies, setPolicies] = useState([]);
    const [cancellationPolicyId, setCancellationPolicyId] = useState('');
    const [cancellationFee, setCancellationFee] = useState(0);
    const [depositPaid, setDepositPaid] = useState(0);
    const [refundDue, setRefundDue] = useState(0);
    const [ruleDescription, setRuleDescription] = useState('');
    const [cancellationReason, setCancellationReason] = useState('');
    const [reasonsList, setReasonsList] = useState([]);
    const [comments, setComments] = useState('');
    const [isCalculating, setIsCalculating] = useState(false);

    useEffect(() => {
        if (open) {
            setError(null);
            setCancellationFee(0);
            setCancellationReason('');
            setComments('');
            fetchPolicies();
            fetchReasons();
        }
    }, [open, tenantId]);

    const fetchPolicies = async () => {
        try {
            const data = await SettingsService.getCancellationPolicies(tenantId);
            const policiesData = Array.isArray(data) ? data : (data.data || []);
            setPolicies(policiesData);
            
            if (reservation?.cancellationPolicyId) {
                setCancellationPolicyId(reservation.cancellationPolicyId);
                return;
            }
            
            try {
                const defaultPolicy = await SettingsService.getGlobalDefaultCancellationPolicy(tenantId);
                if (defaultPolicy && (defaultPolicy.id || defaultPolicy._id)) {
                    setCancellationPolicyId(defaultPolicy.id || defaultPolicy._id);
                    return;
                }
            } catch (defaultErr) {
                console.error('Failed to fetch default cancellation policy', defaultErr);
            }

            if (policiesData.length > 0) {
                setCancellationPolicyId(policiesData[0].id || policiesData[0]._id);
            } else {
                setCancellationPolicyId('');
            }
        } catch (err) {
            console.error('Failed to fetch cancellation policies', err);
            setCancellationPolicyId('');
        }
    };

    const fetchReasons = async () => {
        try {
            const data = await SettingsService.getCancellationReasons(tenantId);
            const reasonsData = Array.isArray(data) ? data : (data.data || []);
            setReasonsList(reasonsData.filter(r => r.isActive !== false));
        } catch (err) {
            console.error('Failed to fetch cancellation reasons', err);
            // Fallback just in case
            setReasonsList([{ id: 'fallback', label: 'Change of Plans' }]);
        }
    };

    useEffect(() => {
        if (open && reservation && cancellationPolicyId) {
            calculateFees();
        }
    }, [open, cancellationPolicyId, reservation]);

    const calculateFees = async () => {
        if (!reservation?.id) return;
        setIsCalculating(true);
        setError(null);
        try {
            // 1. Fetch Reservation Details
            let resData;
            try {
                if (reservation.bookingType === 'Group' || reservation.isGroup) {
                    resData = await ReservationService.getGroupDetail(reservation.groupId || reservation.id, tenantId);
                } else {
                    resData = await ReservationService.getById(reservation.id, tenantId);
                }
            } catch (fetchErr) {
                console.warn('Could not fetch reservation details by ID, falling back to reservation prop:', fetchErr);
                resData = reservation;
            }

            // 1. Extract deposit (advance payment collected from guest)
            const dataObj = resData?.data || resData || reservation || {};
            const deposit = Number(
                dataObj.billing?.finalReservationTotal
            );
            const checkInDateStr = dataObj.checkIn || dataObj.arrivalDate || dataObj.checkInDate || reservation.checkIn || reservation.arrivalDate;
            
            setDepositPaid(deposit);

            // 2. Extract final reservation total amount (to calculate cancellation fee)
            const finalReservationTotal = Number(
                reservation.billing?.finalReservationTotal || dataObj.billing?.finalReservationTotal 
            );

            // 3. Find Policy Rules
            const selectedPolicy = policies.find(p => (p.id || p._id) === cancellationPolicyId);
            const rules = selectedPolicy?.rules || [];

            let penalty = 0;
            let appliedRuleDesc = 'Free Cancellation (No applicable rules found)';

            if (checkInDateStr && rules.length > 0) {
                const checkInDate = new Date(checkInDateStr);
                const now = new Date();
                const diffHours = (checkInDate - now) / (1000 * 60 * 60);

                const doesRuleMatch = (ruleTime) => {
                    if (ruleTime === 'More than 7 days' && diffHours > 168) return true;
                    if (ruleTime === '3 - 7 days' && diffHours <= 168 && diffHours > 72) return true;
                    if (ruleTime === '24 - 72 hours' && diffHours <= 72 && diffHours > 24) return true;
                    if (ruleTime === 'Less than 24 hours' && diffHours <= 24 && diffHours >= 0) return true;
                    if (ruleTime === 'No Show' && diffHours < 0) return true;
                    return false;
                };

                const matchedRule = rules.find(r => doesRuleMatch(r.timeBeforeCheckIn));

                if (matchedRule) {
                    const feeAmount = parseFloat(matchedRule.feeAmount || 0);
                    if (matchedRule.feeType === 'Percentage') {
                        penalty = (finalReservationTotal * feeAmount) / 100;
                    } else {
                        penalty = feeAmount;
                    }
                    appliedRuleDesc = `Applied Rule: ${matchedRule.timeBeforeCheckIn} (${matchedRule.feeType === 'Percentage' ? feeAmount + '%' : 'Fixed ' + currencySymbol + feeAmount})`;
                }
            }
            
            setCancellationFee(penalty);
            setRefundDue(Math.max(0, deposit - penalty));
            setRuleDescription(appliedRuleDesc);

        } catch (err) {
            console.error('Error calculating cancellation fees', err);
            setError('Could not fetch reservation details to calculate fees.');
            setCancellationFee(0);
            setDepositPaid(0);
            setRefundDue(0);
            setRuleDescription('Manual calculation required.');
        } finally {
            setIsCalculating(false);
        }
    };

    const handleConfirm = async () => {
        if (!cancellationReason) {
            setError('Please select a cancellation reason.');
            return;
        }

        setIsCancelling(true);
        setError(null);
        try {
            await ReservationService.cancel(reservation.id, tenantId, {
                cancellationPolicyId,
                cancellationFee: Number(cancellationFee),
                depositAmount: Number(depositPaid),
                reasonId: cancellationReason,
                comments: comments.trim()
            });
            if (setNotification) {
                setNotification({ open: true, message: 'Reservation successfully cancelled and processing fees logged', severity: 'success' });
            }
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to cancel reservation';
            if (setNotification) {
                setNotification({ open: true, message: msg, severity: 'error' });
            } else {
                setError(msg);
            }
        } finally {
            setIsCancelling(false);
        }
    };

    if (!reservation) return null;

    return (
        <Dialog 
            open={open} 
            onClose={isCancelling ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ 
                sx: { 
                    position: 'relative',
                    borderRadius: 3.5, 
                    p: { xs: 2.5, sm: 3 },
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
                } 
            }}
        >
            {/* Top-Right Close Button */}
            <IconButton
                onClick={onClose}
                disabled={isCancelling}
                size="small"
                sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    color: '#94a3b8',
                    '&:hover': { color: '#475569', bgcolor: '#f1f5f9' }
                }}
                aria-label="close"
            >
                <CloseIcon fontSize="small" />
            </IconButton>

            <Box sx={{ mb: 0.75, pr: 4 }}>
                <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.25rem' }}>
                    Cancel Reservation
                </Typography>
            </Box>
            <Typography sx={{ color: '#64748b', fontSize: '0.875rem', mb: 2.5 }}>
                Please select a cancellation reason and policy.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {/* 1. Cancellation Reason */}
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem', mb: 0.75 }}>
                        Cancellation Reason <span style={{ color: '#ef4444' }}>*</span>
                    </Typography>
                    <Select
                        fullWidth
                        size="small"
                        value={cancellationReason}
                        onChange={(e) => setCancellationReason(e.target.value)}
                        disabled={isCancelling}
                        displayEmpty
                        sx={{
                            borderRadius: 2,
                            bgcolor: '#ffffff',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#cbd5e1'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#94a3b8'
                            }
                        }}
                    >
                        <MenuItem value="" disabled sx={{ color: '#94a3b8' }}>
                            Select reason
                        </MenuItem>
                        {reasonsList.map(r => {
                            const idVal = r.id || r._id || r.label || r.name || r.reason || r;
                            const displayVal = r.label || r.name || r.reason || r;
                            return (
                                <MenuItem key={idVal} value={idVal}>
                                    {displayVal}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </Box>

                {/* 2. Cancellation Policy */}
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem', mb: 0.75 }}>
                        Cancellation Policy <span style={{ color: '#ef4444' }}>*</span>
                    </Typography>
                    <Select
                        fullWidth
                        size="small"
                        value={cancellationPolicyId || ''}
                        onChange={(e) => setCancellationPolicyId(e.target.value)}
                        disabled={isCancelling || isCalculating}
                        displayEmpty
                        sx={{
                            borderRadius: 2,
                            bgcolor: '#ffffff',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#cbd5e1'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#94a3b8'
                            }
                        }}
                    >
                        {policies.map(p => (
                            <MenuItem key={p.id || p._id} value={p.id || p._id}>
                                {p.name || p.policyName || 'Unnamed Policy'}
                            </MenuItem>
                        ))}
                        {policies.length === 0 && (
                            <MenuItem value="">No policies available</MenuItem>
                        )}
                    </Select>
                </Box>

                {/* 3. Additional Comments */}
                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem', mb: 0.75 }}>
                        Additional Comments
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        disabled={isCancelling}
                        placeholder="e.g. Guest requested via email"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                bgcolor: '#f8fafc',
                                '& fieldset': {
                                    borderColor: '#e2e8f0'
                                },
                                '&:hover fieldset': {
                                    borderColor: '#cbd5e1'
                                }
                            }
                        }}
                    />
                </Box>

                {/* 4. Fee Breakdown Box */}
                <Box sx={{
                    p: 2,
                    bgcolor: '#fef2f2',
                    border: '1px solid #fca5a5',
                    borderRadius: 2,
                    mb: 3
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: '0.875rem', color: '#334155', fontWeight: 500 }}>
                            Cancellation Fee:
                        </Typography>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>
                            {isCalculating ? 'Calculating...' : formatCurrency(cancellationFee, currency)}
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 1, borderColor: '#fee2e2' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#dc2626' }}>
                            Total Amount:
                        </Typography>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#dc2626' }}>
                            {isCalculating ? 'Calculating...' : formatCurrency(cancellationFee, currency)}
                        </Typography>
                    </Box>
                </Box>

                {/* 5. Action Buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1.5 }}>
                    <Button
                        onClick={onClose}
                        variant="text"
                        disabled={isCancelling}
                        sx={{
                            color: '#475569',
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            p: 0.5,
                            minWidth: 'auto',
                            '&:hover': {
                                bgcolor: 'transparent',
                                color: '#1e293b'
                            }
                        }}
                    >
                        Keep Reservation
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        disabled={isCancelling || isCalculating}
                        sx={{
                            bgcolor: '#dc2626',
                            '&:hover': { bgcolor: '#b91c1c' },
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            px: 3.5,
                            py: 1.1,
                            boxShadow: 'none'
                        }}
                    >
                        {isCancelling ? <CircularProgress size={20} color="inherit" /> : 'Confirm Cancel'}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}
